/**
 * Online Food Ordering Footer Component
 * GDPR compliant footer with privacy links
 */

import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Divider,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import nexfahLogo from '@/assets/nexfah_logo.png';

import { useAppStore } from '@/store/useAppStore';

export default function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const restaurant = useAppStore((state) => state.restaurant);
  const language = useAppStore((state) => state.language);
  const { t } = useTranslation();

  const displayName =
    (language === 'ar' && restaurant?.name_translations?.ar) ||
    restaurant?.name_translations?.en ||
    (language === 'ar' ? 'مطبخ سلطان الخليج' : 'Sulthan Gulf Kitchen');
  const displayPhone =
    (language === 'ar' && restaurant?.phone_translations?.ar) || restaurant?.phone_translations?.en;
  const addrEn = restaurant?.address_translations?.en;
  const addrAr = restaurant?.address_translations?.ar;

  // Hide footer on mobile (bottom nav takes its place)
  if (isMobile) {
    return null;
  }

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* Brand & Contact */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' }, minWidth: { md: '250px' } }}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('footer.tagline')}
            </Typography>
            {restaurant && (
              <Box sx={{ mt: 2 }}>
                {language === 'ar' && addrAr ? (
                  <Box dir="rtl">
                    <Typography variant="body2" color="text.secondary">
                      {addrAr.address_line_1 || addrEn?.address_line_1}
                    </Typography>
                    {(addrAr.address_line_2 || addrEn?.address_line_2) && (
                      <Typography variant="body2" color="text.secondary">
                        {addrAr.address_line_2 || addrEn?.address_line_2}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {addrAr.city || addrEn?.city}, {restaurant.postcode}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {addrEn?.address_line_1 && (
                      <Typography variant="body2" color="text.secondary">
                        {addrEn.address_line_1}
                      </Typography>
                    )}
                    {addrEn?.address_line_2 && (
                      <Typography variant="body2" color="text.secondary">
                        {addrEn.address_line_2}
                      </Typography>
                    )}
                    {addrEn?.city && (
                      <Typography variant="body2" color="text.secondary">
                        {addrEn.city}, {restaurant.postcode}
                      </Typography>
                    )}
                  </>
                )}
                {displayPhone && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                    dir={language === 'ar' && restaurant.phone_translations?.ar ? 'rtl' : undefined}
                  >
                    {t('footer.tel')} {displayPhone}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Quick Links */}
          <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 auto' }, minWidth: '120px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('footer.menu_section')}
            </Typography>
            <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link component={RouterLink} to="/menu" color="text.secondary" underline="hover">
                {t('footer.full_menu')}
              </Link>
              <Link
                component={RouterLink}
                to="/menu?featured=true"
                color="text.secondary"
                underline="hover"
              >
                {t('footer.featured_items')}
              </Link>
              <Link
                component={RouterLink}
                to="/menu?popular=true"
                color="text.secondary"
                underline="hover"
              >
                {t('footer.popular_items')}
              </Link>
            </Box>
          </Box>

          {/* Account */}
          <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 auto' }, minWidth: '120px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('footer.account')}
            </Typography>
            <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link component={RouterLink} to="/login" color="text.secondary" underline="hover">
                {t('footer.sign_in')}
              </Link>
              <Link component={RouterLink} to="/orders" color="text.secondary" underline="hover">
                {t('footer.track_order')}
              </Link>
            </Box>
          </Box>

          {/* Legal - GDPR Compliance */}
          <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 auto' }, minWidth: '120px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('footer.legal')}
            </Typography>
            <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link component={RouterLink} to="/privacy" color="text.secondary" underline="hover">
                {t('footer.privacy')}
              </Link>
              <Link component={RouterLink} to="/terms" color="text.secondary" underline="hover">
                {t('footer.terms')}
              </Link>
              <Link component={RouterLink} to="/cookies" color="text.secondary" underline="hover">
                {t('footer.cookies')}
              </Link>
              <Link component={RouterLink} to="/allergens" color="text.secondary" underline="hover">
                {t('footer.allergens')}
              </Link>
            </Box>
          </Box>

          {/* Social */}
          <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 auto' }, minWidth: '120px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('footer.follow')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" color="inherit" aria-label="Facebook">
                <FacebookIcon />
              </IconButton>
              <IconButton size="small" color="inherit" aria-label="Instagram">
                <InstagramIcon />
              </IconButton>
              <IconButton size="small" color="inherit" aria-label="Twitter">
                <TwitterIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('footer.powered')}
            </Typography>
            <Link
              href="https://nexfah.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                '&:hover': { opacity: 0.8 },
                transition: 'opacity 0.2s',
              }}
            >
              <Box
                component="img"
                src={nexfahLogo}
                alt="Nexfah"
                sx={{
                  height: 20,
                  width: 'auto',
                  maxWidth: 100,
                  objectFit: 'contain',
                }}
              />
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
